<template>
  <div v-if="object">
    <LinkButton :u="true" @click="hidden = !hidden">{{ commentCount }}</LinkButton>
    <LinkButton :u="true" @click="showForm" v-if="isLoggedIn">post a comment</LinkButton>
    <router-link class="whitelink" v-if="!isLoggedIn" :to="{name: 'account-login', query: { next: $route.fullPath }}">sign up to leave a comment</router-link>
    <div v-if="!hidden">
      <div class="post" v-if="formVisible">
        <b-form-input ref="input" size="sm" required v-model="text" @keyup.native="onKeyUp"></b-form-input>
        <b-button size="sm" class="send" @click="send">Send</b-button>
        <LinkButton :u="true" @click="formVisible = false">cancel</LinkButton>
      </div>

      <b-container v-if="comments">
        <b-row v-for="c in comments" :key="c.id">
          <b-col class="comment">
            <router-link v-if="c.artist" :to="{name: 'artist-about', params: {artistId: c.artist.id}}"><img :src="c.artist.avatar_image" :class="avatarClass"></router-link>
            <router-link v-else :to="{name: 'fan-profile', params: {id: c.fan.id}}"><img :src="avatarImage(c.fan)" :class="avatarClass"></router-link>
            <div>
              <div class="meta">
                <router-link v-if="c.artist" class="who" :to="{name: 'artist-about', params: {artistId: c.artist.id}}">{{ c.artist.name }}</router-link>
                <router-link v-else class="who" :to="{name: 'fan-profile', params: {id: c.fan.id}}">{{ displayName(c.fan) }}</router-link>
                {{ formatDate(c) }}
              </div>
              <div v-html="formatText(c)">
              </div>
            </div>
          </b-col>
          <b-col cols="1" class="text-right">
            <Report label="Report comment" :comment="c" :update="update" :live="live" :video="video" class="report-link">
              <LinkButton v-if="canDeleteComment(c)" @click="deleteComment(c)">Delete comment</LinkButton>
              <LinkButton v-if="canManage" @click="blockUser(c.fan)">Block user</LinkButton>
            </Report>
          </b-col>
        </b-row>
      </b-container>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @import '@/assets/custom.scss';

  .ellipsis {
    padding: 0 5px;
    line-height: 0.5em;
    border-radius: 0;
    margin-top: 0.5em;
    margin-bottom: 5px;
    background: $darkish-pink;
    border: $dark-pink;
  }

  .whitelink {
    color: white;
  }

  .count {
    color: $steel;
    margin-right: 1em;
  }

  .post {
    display: flex;
    align-items: center;
    margin-top: 0.5em;

    input {
      display: inline-block;
      flex: 1;
    }

    .send {
      margin-left: 1em;
      margin-right: 1em;
    }
  }

  .comment {
    display: flex;
    margin-top: 0.5em;
    margin-bottom: 0.5em;

    img {
      border-radius: 100%;
      margin-right: 10px;
      width: 40px;
      height: 40px;

      &.small {
        width: 32px;
        height: 32px;
      }
    }

    .meta {
      color: $steel;
    }
  }

  .report-link {
    /deep/ svg {
      color: $steel;

      &:hover {
        color: white;
      }
    }
  }
</style>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import moment from '@/utils/moment'

import FontAwesomeIcon from '@fortawesome/vue-fontawesome'

import LinkButton from '@/components/widgets/LinkButton'
import Report from '@/components/widgets/Report'

import { profileHelpers } from '@/store/helpers'
import { linkifyHandles } from '@/utils/textformat'

export default {
  name: 'Comments',
  components: {
    LinkButton,
    Report,
    FontAwesomeIcon
  },
  props: {
    update: Object,
    live: Object,
    video: Object,
    small: Boolean,
    canManage: Boolean,
    collapsed: Boolean
  },
  model: {
    prop: 'comments',
    event: 'change'
  },
  data () {
    return {
      comments: null,
      formVisible: false,
      text: '',
      hidden: true
    }
  },
  computed: {
    object () {
      if (this.update) return this.update
      if (this.live) return this.live
      if (this.video) return this.video
      return null
    },
    commentCount () {
      if (this.comments === null) {
        if (this.video) return this.formatCommentCount(this.video.comment_count)
        if (this.live) return this.formatCommentCount(this.live.comment_count)
        if (this.update) return this.formatCommentCount(this.update.comments ? this.update.comments.length : 0)
        return 'show comments'
      }
      return this.formatCommentCount(this.comments.length)
    },
    avatarClass () {
      return this.small ? 'small' : ''
    },
    ...mapGetters('auth', ['isLoggedIn']),
    ...mapGetters('profile', ['currentUser'])
  },
  created () {
    this.hidden = this.collapsed
    this.initComponent()
  },
  watch: {
    'object': {
      handler: function (newValue, oldValue) {
        if (this.live && newValue.comment_count === oldValue.comment_count) return
        this.initComponent()
      }
    },
    'hidden': 'initComponent'
  },
  methods: {
    async initComponent () {
      if (this.hidden) return
      this.formVisible = false
      this.text = ''

      if (this.update) {
        this.comments = this.update.comments
      } else if (this.live) {
        this.comments = await (await this.$store.dispatch('live/getLiveEventComments', {id: this.live.id})).all()
      } else if (this.video) {
        this.comments = await (await this.$store.dispatch('song/getSongComments', {id: this.video.id})).all()
      } else {
        this.comments = []
      }
    },
    formatCommentCount (count) {
      if (count === 0) return 'no comments'
      if (count === 1) return '1 comment'
      return count + ' comments'
    },
    formatDate (item) {
      return moment(item.created_at).fromNow()
    },
    formatText (item) {
      return linkifyHandles(item.text || '')
    },
    showForm () {
      this.hidden = false
      Vue.nextTick(() => {
        this.formVisible = true
      })
    },
    onKeyUp (ev) {
      if (ev.key !== 'Enter') return
      this.send()
    },
    async send () {
      if (!this.text) return

      let text = this.text.trim()
      let data = null

      if (this.update) {
        data = await this.$store.dispatch('feed/postUpdateComment', {id: this.update.id, text: text, asArtist: this.canManage})
      } else if (this.live) {
        data = await this.$store.dispatch('live/postLiveEventComment', {id: this.live.id, text: text, asArtist: this.canManage})
      } else if (this.video) {
        data = await this.$store.dispatch('song/postSongComment', {id: this.video.id, text: text, asArtist: this.canManage})
      }

      if (data) {
        this.comments.unshift(data)
        if (this.video) this.video.comment_count++
        if (this.live) this.live.comment_count++
      }

      this.formVisible = false
      this.text = ''
    },
    canDeleteComment (comment) {
      return this.canManage || comment.fan.id === this.currentUser.id
    },
    async deleteComment (comment) {
      if (!this.canDeleteComment(comment)) return

      if (this.update) {
        await this.$store.dispatch('feed/deleteUpdateComment', {updateId: this.update.id, commentId: comment.id})
      } else if (this.live) {
        await this.$store.dispatch('live/deleteLiveEventComment', {eventId: this.live.id, commentId: comment.id})
      } else if (this.video) {
        await this.$store.dispatch('song/deleteSongComment', {songId: this.video.id, commentId: comment.id})
      }

      let idx = this.comments.indexOf(comment)
      if (idx !== -1) this.comments.splice(idx, 1)

      this.$store.dispatch('flash/showMessage', {message: 'Comment deleted'})

      if (this.video) this.video.comment_count--
      if (this.live) this.live.comment_count--
    },
    async blockUser (fan) {
      if (!this.canManage) return

      await this.$store.dispatch('profile/blockProfile', { id: fan.id })
      this.$store.dispatch('flash/showMessage', { message: 'User blocked' })

      this.initComponent()
    },
    ...profileHelpers
  }
}
</script>



// WEBPACK FOOTER //
// src/components/widgets/Comments.vue